import { useConcurrencyServiceWithFetch } from '../../../hooks/useConcurrencyService';
import useNetwork from '../../../hooks/useNetwork';
import { useContext, useEffect, useState } from 'react';
import { getStreamConcurrencyDisplayError } from '../../../utils/error';
import { playerTypes } from '../constants';
import { fetchStreamUrl, useQueueInterval, handleError, type AdBlockSSAI } from '../utils/PlayerUtills';
import cdnHandler from '../../../helpers/CDNHandler';
import { isSafari } from '../../../helpers/browser';
import { avodHandler } from '../../../helpers/avodHandler';
import { getMixpanelVodProperties, getMixpanelLinearProperties } from '../../../contexts/MixpanelHelper';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import useAvod from '../../../hooks/useAvod';
import { __ } from '../../../translations';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import useCSAI from '../../../hooks/useCSAI';
import { EVENT } from '../../../contexts/MixpanelConstants';
import user from '../../../helpers/user';
import { isFreeTier } from '@intigral/utils/lib/user';
import { playerSSAIManager } from '../../../utils/SSAI';
import url from '../../../helpers/url';

interface AdEligibilityVariables {
    enableAVOD: boolean;
    enableAdsKidsMode: boolean;
    isLinearAdsEnabled: boolean;
    isRewindAdsEnabled: boolean;
    adTechnology?: 'CSAI' | 'SSAI';
    contentType: 'vod' | 'linear';
    businessRuleEligibility: boolean;
    playerPlaybackType: 'CHANNEL' | 'ASSET';
    isKidsMode: boolean;
    isFreeTier: boolean;
    userOperator: string;
    isOperatorAdsEnabled: boolean;
    isToPlayFromBookmark: boolean;
    isContentAdEnabled: boolean;
}

export const useConcurency = (
    streamUrl,
    playerType,
    isTvod,
    VODAsset,
    LinearAsset,
    streamingType,
    bookmarkedVodTimeCode,
    setAutoUiVariantResolve,
    onDestroy,
    setState
) => {
    // state
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [streamError, setStreamError] = useState<any>(null);
    const [currentLockId, setCurrentLockId] = useState<string | null>(() => sessionStorage.getItem('lockId'));
    const [nextDelay, setNextDelay] = useState<number | null>(null);

    // configuration from React Query
    const { video = {}, config } = useConfigurationSections();
    const concurrencyConfig = (video as any)?.streamConcurrencyV2 || {};
    const isCatchupConCurrencyEnable = concurrencyConfig?.catchup?.enable || false;
    const isVodConCurrencyEnable = concurrencyConfig?.vod?.enable || false;
    const isLinearCurrencyEnable = concurrencyConfig?.linear?.enable || false;
    const allowConcurrencyFallback = concurrencyConfig?.allowConcurrencyFallback || false;
    const allowTokenValidationFallback = concurrencyConfig?.allowTokenValidationFallback || false;

    // hooks
    const { unLock, acquireLock, refreshLock } = useConcurrencyServiceWithFetch();
    const [isOnline] = useNetwork();
    const { track } = useContext(MixpanelContext);

    const avodConfigs: any = (config as any)?.avodConfigs || {};
    const userInfo = user.getUserInfo();
    const isFreeTierSub = isFreeTier(userInfo);
    const userOperator = (userInfo?.operator || '').toUpperCase();
    const isOperatorWithAdsEnabled = (config as any)?.operators?.[userOperator]?.isAdsEnabled ?? false;

    useEffect(() => {
        if (streamError) {
            const errorMessage = streamError?.message ?? '';
            const title = __(errorMessage + '_title');
            const desc = __(errorMessage + '_desc');
            handleError(
                {
                    title: title === errorMessage + '_title' ? __('general_error_message') : title,
                    description: desc === errorMessage + '_desc' ? errorMessage : desc,
                },
                onDestroy,
                setState
            );
        }
    }, [streamError]);

    const clearConcurrentLock = async () => {
        const lockId = sessionStorage.getItem('lockId');
        if (lockId) {
            try {
                await unLock(lockId);
            } catch (error) {
                console.warn('Failed to clear concurrency lock', error);
            } finally {
                sessionStorage.removeItem('lockId');
                setCurrentLockId(null);
            }
        }
    };

    const refreshConcurrencyLock = async () => {
        if (!isOnline || !currentLockId) {
            setNextDelay(null);
            return;
        }
        try {
            const response = await refreshLock(null, currentLockId);
            if (response?.data) {
                const timeoutDiff = response.data.lockExpiryMs - response.data.lockCreationMs;
                setNextDelay(timeoutDiff);
                sessionStorage.setItem('lockId', response.data.lockId);
                setCurrentLockId(response.data.lockId);
            } else if (response?.error) {
                const { errorTitle, errorDesc } = getStreamConcurrencyDisplayError(response?.error);
                handleError({ title: errorTitle, description: errorDesc }, onDestroy, setState);
                setNextDelay(null);
            }
        } catch (error) {
            setStreamError(error);
            setNextDelay(null);
        }
    };

    const trackPreRollAdEligibleContent = () => {
        const isContentVod = playerType === playerTypes.Vod;
        const isLinear = playerType === playerTypes.LiveTV;
        const isRewind = playerType === playerTypes.Catchup;

        if (isContentVod) {
            const hasBookmark = typeof bookmarkedVodTimeCode === 'function' ? bookmarkedVodTimeCode() !== 0 : false;
            const isAdEligible = isContentVod && isAdsEnabled && !hasBookmark;
            const isKidsMode = Array.isArray(VODAsset?.categoryType) && VODAsset.categoryType.includes('kids');
            const AdEligibilityVariables: AdEligibilityVariables = {
                enableAVOD: avodConfigs?.enableAVOD ?? false,
                enableAdsKidsMode: avodConfigs?.enableAVODKidsMode ?? false,
                isLinearAdsEnabled: false,
                isRewindAdsEnabled: false,
                contentType: 'vod',
                adTechnology: 'SSAI',
                businessRuleEligibility: !isAdEligible,
                playerPlaybackType: 'ASSET',
                isKidsMode,
                isFreeTier: isFreeTierSub,
                userOperator,
                isOperatorAdsEnabled: isOperatorWithAdsEnabled,
                isToPlayFromBookmark: hasBookmark,
                isContentAdEnabled: Boolean(VODAsset?.adEnabled),
            };

            const mixpanelProperties = VODAsset ? getMixpanelVodProperties(VODAsset) : {};

            track(EVENT.AVOD.PRE_ROLL_AD_ELIGIBLE_CONTENT, {
                ...mixpanelProperties,
                'Is Ad Eligible': isAdEligible,
                'Ad Decision Eligibility Variables': AdEligibilityVariables,
                'Business Rule Eligibility': !isAdEligible,
            });
        } else {
            const contentType = isLinear || isRewind ? 'linear' : 'vod';
            const isAdEligible = !!isAdsEnabledForCSAI;
            const isKidsMode = Boolean(
                (LinearAsset as any)?.categoryType?.includes('kids') ||
                    (LinearAsset as any)?.channel?.category?.includes('kids')
            );

            const AdEligibilityVariables: AdEligibilityVariables = {
                enableAVOD: avodConfigs?.enableAVOD ?? false,
                enableAdsKidsMode: avodConfigs?.enableAVODKidsMode ?? false,
                isLinearAdsEnabled: isLinear && isAdsEnabledForCSAI,
                isRewindAdsEnabled: isRewind && isAdsEnabledForCSAI,
                adTechnology: 'CSAI',
                contentType: 'linear',
                businessRuleEligibility: !isAdsEnabledForCSAI,
                playerPlaybackType: isLinear ? 'CHANNEL' : 'ASSET',
                isKidsMode,
                isFreeTier: isFreeTierSub,
                userOperator,
                isOperatorAdsEnabled: isOperatorWithAdsEnabled,
                isToPlayFromBookmark: false,
                isContentAdEnabled: Boolean(
                    isLinear ? (LinearAsset as any)?.channel?.adEnabled : LinearAsset?.adEnabled
                ),
            };

            const mixpanelProperties =
                contentType === 'vod'
                    ? VODAsset
                        ? getMixpanelVodProperties(VODAsset)
                        : {}
                    : LinearAsset
                    ? getMixpanelLinearProperties(LinearAsset)
                    : {};

            track(EVENT.AVOD.PRE_ROLL_AD_ELIGIBLE_CONTENT, {
                ...mixpanelProperties,
                'Is Ad Eligible': isAdEligible,
                'Ad Decision Eligibility Variables': AdEligibilityVariables,
                'Business Rule Eligibility': !isAdsEnabledForCSAI,
            });
        }
    };

    const handleAVOD = () => {
        const isContentVod = playerType === playerTypes.Vod;
        const hasBookmark = typeof bookmarkedVodTimeCode === 'function' ? bookmarkedVodTimeCode() !== 0 : false;
        const isAdEligible = isContentVod && isAdsEnabled && !hasBookmark;

        if (isContentVod && isAdEligible && VODAsset) {
            avodHandler.initialize(track, getMixpanelVodProperties(VODAsset));
            setupAVODParams();
            setAutoUiVariantResolve(false);
        }
    };

    const handleCSAIAds = () => {
        if (!isAdsEnabledForCSAI) return;

        setupAdsParamsForCSAI();

        if (playerType === playerTypes.LiveTV && LinearAsset) {
            avodHandler.initialize(track, getMixpanelLinearProperties(LinearAsset), true);
        }
    };

    const handleSSAI = async (finalUrl: string) => {
        if (!isFreeTierSub || playerType !== playerTypes.Vod) return;
        const response = await fetch(finalUrl);
        const manifestXml = await response.text();
        playerSSAIManager.extractAdPeriods(manifestXml);
    };

    // if streamConcurency is not enable or use as fallback stream if stream harness api fails
    const geFallbacktUnTokenizedStreamUrl = async (cdnSession?: unknown) => {
        try {
            const finalUrl = await fetchStreamUrl(streamUrl, playerType, cdnSession);
            await handleSSAI(finalUrl);
            setResolvedUrl(finalUrl);
        } catch (error) {
            setStreamError(error);
        }
    };

    // --- NEW CONCURRENCY LOGIC ---
    /**
     * @param cdnSession The player's own SmartLib session (`cdnHandler.initiateSession`): the stream is
     * resolved on it, so a resolve still queued when the player unmounts does not open a new one.
     */
    const resolveInitialStream = async (isSwitch, cdnSession?: unknown) => {
        if (!isOnline || !streamUrl) return;

        trackPreRollAdEligibleContent();
        handleAVOD();
        handleCSAIAds();

        const needsConcurrency =
            (playerType === playerTypes.Catchup && isCatchupConCurrencyEnable) ||
            (playerType === playerTypes.Vod && isVodConCurrencyEnable) ||
            (playerType === playerTypes.LiveTV && isLinearCurrencyEnable);

        if (!needsConcurrency) {
            // if the stream concurency is false then only trailer can play other stream will not be played and player show general error nessage
            if (playerType === playerTypes.Trailer) {
                geFallbacktUnTokenizedStreamUrl(cdnSession);
            } else {
                setStreamError({ title: 'general_error_message' });
            }
            return;
        }

        const streamType = playerType === playerTypes.LiveTV ? streamingType || 'live' : playerType.toLowerCase();
        const payload: { publicUrl: any; switch: string; type: any; ll: string } = {
            publicUrl: streamUrl,
            switch: isSafari() ? 'fairplay' : 'dash',
            type: streamType,
            ll: url.getQuery('ll') || '',
        };

        try {
            let response = null;
            if (isSwitch && currentLockId) {
                response = await refreshLock(payload, currentLockId);
            } else {
                await clearConcurrentLock();
                response = await acquireLock(payload);
            }
            if (response?.data) {
                sessionStorage.setItem('lockId', response.data.lockId);
                setCurrentLockId(response.data.lockId);
                const { streamContent, t: jwt, lockExpiryMs, lockCreationMs } = response.data;
                const concurrencyJwt = concurrencyConfig?.contentPrivacy?.enable ? jwt : null;
                const finalUrl = await cdnHandler.resolveURL(
                    streamContent?.streamingUrl,
                    concurrencyJwt,
                    undefined,
                    cdnSession
                );
                await fetch(finalUrl)
                    .then((res) => {
                        if (res?.status === 200) {
                            return res.text();
                        }
                        // Reject if the response is not successful to trigger the fallback (catch block).
                        return Promise.reject(new Error(`Failed to fetch stream URL, status code: ${res.status}`));
                    })
                    .then((manifestXml) => {
                        if (isFreeTierSub || playerType === playerTypes.Vod)
                            playerSSAIManager.extractAdPeriods(manifestXml);
                        setResolvedUrl(finalUrl);
                        const timeoutDiff = lockExpiryMs - lockCreationMs;
                        setNextDelay(timeoutDiff);
                    })
                    .catch((err) => {
                        // handle fallback to un-tokenized stream url if fallback and token fallback is on
                        if (allowConcurrencyFallback && allowTokenValidationFallback) {
                            geFallbacktUnTokenizedStreamUrl(cdnSession);
                            return;
                        } else {
                            setStreamError(err);
                        }
                    });
            } else if (response?.error) {
                if (response?.status === 401 || response?.status === 403 || response?.status === 428) {
                    const { errorTitle, errorDesc } = getStreamConcurrencyDisplayError(response?.error);
                    handleError({ title: errorTitle, description: errorDesc }, onDestroy, setState);
                } else {
                    // handle fallback to un-tokenized stream url if the config is enabled
                    if (allowConcurrencyFallback) {
                        geFallbacktUnTokenizedStreamUrl(cdnSession);
                    } else {
                        const { errorTitle, errorDesc } = getStreamConcurrencyDisplayError(response?.error);
                        handleError({ title: errorTitle, description: errorDesc }, onDestroy, setState);
                    }
                }
                setNextDelay(null);
            }
        } catch (error) {
            if (allowConcurrencyFallback) {
                geFallbacktUnTokenizedStreamUrl(cdnSession);
            } else {
                setStreamError(error);
            }
        }
    };

    const { isAdsEnabledForCSAI, setupAdsParamsForCSAI } = useCSAI(LinearAsset || VODAsset, playerType, 'LINEAR');
    const { setupAVODParams, isAdsEnabled } = useAvod(playerType, isTvod, VODAsset);
    const { start, stop } = useQueueInterval(refreshConcurrencyLock);

    useEffect(() => {
        if (nextDelay !== null && nextDelay > 0 && streamUrl) {
            start(nextDelay);
        } else {
            stop();
        }
        return () => stop();
    }, [nextDelay, streamUrl, start, stop]);

    return {
        resolvedUrl,
        clearConcurrentLock,
        refreshConcurrencyLock,
        resolveInitialStream,
        setNextDelay,
    };
};
